import { trackCityScopeEvent, type CityScopeJourney } from './analyticsJourneys';

export type CoreJourney = Extract<
  CityScopeJourney,
  'planning_research' | 'reports' | 'account' | 'engagement'
>;

export interface CoreJourneyEventContext {
  source?: string | null;
  placement?: string | null;
  status?: string | null;
  reason?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  value?: number | null;
  metadata?: Record<string, unknown>;
}

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/i;
const IPV4_PATTERN = /\b(?:\d{1,3}\.){3}\d{1,3}\b/;

function cleanEventPart(value: string | null | undefined, fallback: string): string {
  const raw = (value || '').trim().toLowerCase();
  if (!raw || EMAIL_PATTERN.test(raw) || IPV4_PATTERN.test(raw)) return fallback;

  const cleaned = raw
    .replace(/https?:\/\/[^/\s]+/g, '')
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

  return cleaned || fallback;
}

function cleanMetadataValue(value: unknown): string | number | boolean | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;

  const cleaned = cleanEventPart(value, 'redacted');
  return cleaned === 'redacted' ? undefined : cleaned;
}

function buildMetadata(journey: CoreJourney, context: CoreJourneyEventContext): Record<string, unknown> {
  const metadata: Record<string, unknown> = {
    journey,
    source: cleanEventPart(context.source, 'direct'),
  };

  if (context.placement) metadata.placement = cleanEventPart(context.placement, 'unknown_placement');
  if (context.status) metadata.status = cleanEventPart(context.status, 'unknown_status');
  if (context.reason) metadata.reason = cleanEventPart(context.reason, 'unknown_reason');
  if (context.entityType) metadata.entity_type = cleanEventPart(context.entityType, 'unknown_entity_type');
  if (context.entityId) metadata.entity_id = cleanEventPart(context.entityId, 'unknown_entity');

  for (const [key, value] of Object.entries(context.metadata || {})) {
    const cleanKey = cleanEventPart(key, 'metadata_key');
    const cleanValue = cleanMetadataValue(value);
    if (cleanValue !== undefined) metadata[cleanKey] = cleanValue;
  }

  return metadata;
}

export function buildCoreJourneyEventName(
  journey: CoreJourney,
  action: string,
  context: CoreJourneyEventContext = {},
): string {
  return [
    cleanEventPart(context.source, journey),
    context.placement ? cleanEventPart(context.placement, 'placement') : null,
    context.entityType ? cleanEventPart(context.entityType, 'entity') : null,
    context.entityId ? cleanEventPart(context.entityId, 'entity_id') : null,
    context.status ? cleanEventPart(context.status, 'status') : null,
    context.reason ? cleanEventPart(context.reason, 'reason') : null,
  ]
    .filter(Boolean)
    .join(':') || cleanEventPart(action, journey);
}

export function trackCoreJourneyEvent(
  journey: CoreJourney,
  action: string,
  context: CoreJourneyEventContext = {},
): void {
  trackCityScopeEvent({
    category: `journey:${journey}`,
    action,
    name: buildCoreJourneyEventName(journey, action, context),
    value: typeof context.value === 'number' ? context.value : undefined,
    metadata: buildMetadata(journey, context),
  });
}
